import {useEffect, useState} from 'react'
import type {PropsWithChildren, JSX} from 'react'
import App from 'next/app'
import type {AppContext, AppProps} from 'next/app'
import {ReactQueryDevtools} from 'react-query/devtools'
import {QueryClient, QueryClientProvider} from 'react-query'
import dynamic from 'next/dynamic'
import {useRouter} from 'next/router'
import {clsx} from 'clsx'
import '../styles/globals.css'
import Script from 'next/script'
import {GoogleTagManager} from '@next/third-parties/google'
import findLastIndex from 'array.prototype.findlastindex'
import {SmartLinkProvider} from '@/services/kontent-ai/preview-mode/smart-link'
import type {PageProps} from '@/pages/[[...slug]]'
import {useMikMak} from '@/services/mikmak'
import {AuthProvider} from '@/products/auth/auth-provider'
// Needed for rich-text-renderer
import {useTrustArcConsent} from '@/services/trustarc'
import {disablePageView, enablePageView} from '@/services/logger/client'
import {useBazaarVoice} from '@/services/bazaar-voice'

findLastIndex.shim()

const BackToTop = dynamic(
	// eslint-disable-next-line @typescript-eslint/ban-ts-comment, @typescript-eslint/prefer-ts-expect-error -- Necessary to fix build
	// @ts-ignore -- Necessary to fix build
	() =>
		import('@/products/flexible-web-toolkit/components/back-to-top-button').then(
			(mod) => mod.BackToTopButton
		),
	{
		ssr: false,
	}
)

const Layout = (props: PropsWithChildren<PageProps>): JSX.Element => {
	const router = useRouter()

	if (router.locale === 'global') {
		return (
			<>
				<div
					className={clsx(
						props.isInPreviewMode ? 'preview-mode' : null,
						'pt-6'
					)}
				/>
				{props.children}
				<BackToTop
					altText={
						props.page.elements.snippetPageSeoBackToTopButtonAltText
					}
					hide={
						props.page.elements.snippetPageSeoHideBackToTopButton[0]
							?.codename === 'hide'
					}
				/>
			</>
		)
	}
	return (
		<div className="flex min-h-screen flex-col">
			{props.children}
			<BackToTop
				altText={
					props.page.elements.snippetPageSeoBackToTopButtonAltText
				}
				hide={
					props.page.elements.snippetPageSeoHideBackToTopButton[0]
						?.codename === 'hide'
				}
			/>
		</div>
	)
}

const Application = ({
	Component,
	pageProps: rawPageProps,
	router,
}: AppProps<PageProps>): JSX.Element => {
	const pageProps = rawPageProps

	const gtmContainerID = pageProps.globalConfig.elements.gtmContainerId.trim()
	const trustBanner = pageProps.globalConfig.elements.trustArcBanner.trim()
	const country = pageProps.globalConfig.elements.country.trim()
	const language = pageProps.globalConfig.elements.language.trim()

	const mikMakAppId = pageProps.globalConfig.elements.appId || null
	useMikMak(mikMakAppId)
	useBazaarVoice(router.locale)

	useEffect(() => {
		if (router.locale === 'global') return
		const taScript = document.createElement('script')
		taScript.src = `https://consent.trustarc.com/notice?domain=${trustBanner}&c=teconsent&js=nj&noticeType=bb&text=true&country=${country}&language=${language}&gtm=1`
		document.head.appendChild(taScript)
		return () => {
			taScript.remove()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only run on locale change
	}, [router.locale])

	useEffect(() => {
		// Function to update lang attribute on the <html> tag during page load and on route change
		const updateLang = (): void => {
			const langHTMLTag = pageProps.globalConfig.elements.langHtmlTag
			if (langHTMLTag && document.documentElement.lang !== langHTMLTag) {
				document.documentElement.setAttribute('lang', langHTMLTag)
			}
		}
		const handleRouteChange = (): void => {
			updateLang()
		}
		router.events.on('routeChangeComplete', handleRouteChange)

		return () => {
			router.events.off('routeChangeComplete', handleRouteChange)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- removing dependency to run only once
	}, [])

	// Create a query client
	// eslint-disable-next-line react/hook-use-state -- React query initialization
	const [queryClient] = useState(() => new QueryClient())

	const cookieConsent = useTrustArcConsent()

	useEffect(() => {
		if (
			cookieConsent === 'ALL_COOKIES' ||
			cookieConsent === 'FUNCTIONAL_COOKIES'
		) {
			enablePageView()
		} else {
			disablePageView()
		}
	}, [cookieConsent])
	if (router.locale === 'null') return <p>404 - Not found</p>

	return (
		<>
			{/* This library is used for autocomplete functionality in the CDC Screenset */}
			<Script
				async
				src="https://cdn.jsdelivr.net/npm/@tarekraafat/autocomplete.js@10.2.7/dist/autoComplete.min.js"
				strategy="afterInteractive"
				type="text/javascript"
			/>
			{gtmContainerID && router.locale !== 'global' ? (
				<GoogleTagManager gtmId={gtmContainerID} />
			) : null}

			<SmartLinkProvider>
				<QueryClientProvider client={queryClient}>
					<AuthProvider {...pageProps}>
						<Layout {...pageProps}>
							<Component {...pageProps} />
						</Layout>
					</AuthProvider>
					<ReactQueryDevtools initialIsOpen={false} />
				</QueryClientProvider>
			</SmartLinkProvider>
		</>
	)
}

Application.getInitialProps = async (appContext: AppContext) => {
	const appProps = await App.getInitialProps(appContext)
	if (appContext.router.locale === 'null') return {notFound: true}
	return {...appProps}
}

export default Application
